import React from 'react';
import { Container, Typography, Box } from '@mui/material';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body1" sx={{ mb: 1 }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Public, unauthenticated page - mirrors the content in
 * 99Yards-Mobile-App/app/screens/profile/PrivacyPolicy.tsx. Exists because
 * Apple App Store Connect / Google Play Console require a public URL for the
 * privacy policy in the store listing, not just an in-app screen.
 */
const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
        Last updated: {lastUpdated}
      </Typography>

      <Section title="Introduction">
        We know that in this digital age, your privacy is important. This Privacy
        Policy describes how 99 YARDS collects, uses, shares, and protects
        information about you when you use the 99 YARDS app and services (the
        "Services"). By using the Services, you agree to the collection and use of
        information as described here. We will review, update, and amend this
        Policy from time to time consistent with our business needs and applicable
        law, and we encourage you to check back periodically. Your continued use of
        the Services after a change means you accept the updated Policy.
      </Section>

      <Section title="Information We Collect">
        We collect user data in the following categories: account and identity data
        (name, email, phone number, password, date of birth, account
        category/subcategory); profile and content data (photos, portfolio images
        and digitals, bios, offers, reviews, and other content you upload); booking
        and payment data (booking details, offer terms, and payment metadata - our
        payment processor handles your full card details, we never see or store
        your raw card number); communications (messages, media, and call metadata
        exchanged through the app's messaging features); location data (city,
        state, and address information you provide); and device/usage data (device
        identifiers, app version, crash logs, and how you interact with the
        Services), collected automatically.
      </Section>

      <Section title="How We Use Your Information">
        We use the information we collect to create and manage your account;
        facilitate bookings, offers, and payments between users; enable messaging
        and calls between users; show your profile, portfolio, and reviews to other
        users; send you service-related notifications and, where permitted,
        marketing communications; detect and prevent fraud and abuse, and enforce
        our Code of Conduct; analyze trends and improve the Services; and comply
        with legal obligations.
      </Section>

      <Section title="AI-Assisted Features">
        Some parts of the Services use artificial intelligence to help you work
        faster - for example, generating a draft job-post description from the
        details you enter. Text you submit to these features is sent to our AI
        service provider (OpenAI) to generate a response. We do not use your
        private messages, payment information, or uploaded media to train AI
        models.
      </Section>

      <Section title="Third Parties We Work With">
        We share information with the following service providers, each of whom
        processes it only to provide their specific service to us: Stripe (payment
        processing and payouts), Firebase / Google Cloud Platform (hosting,
        authentication, database, file storage, and analytics), RevenueCat
        (subscription management), Twilio (voice calls and messaging
        infrastructure), Meta (Facebook) (social login, where you choose to sign in
        with Facebook), Branch (deep linking and install attribution), and OpenAI
        (AI-assisted features). We do not sell your personal information to third
        parties.
      </Section>

      <Section title="Cookies and Similar Technologies">
        We and our service providers use cookies, SDKs, and similar technologies to
        recognize you when you sign in, remember your preferences, detect and
        prevent fraud and improve security, and measure and analyze performance of
        the Services.
      </Section>

      <Section title="Data Retention and Deletion">
        We retain your information for as long as your account is active or as
        needed to provide the Services. When you delete your account from Settings,
        we delete your account profile, portfolio, uploaded media, wallet,
        notifications, and other data that belongs solely to you. Some information
        is not deleted outright because it also belongs to another user or is
        needed for legal/financial recordkeeping: reviews you wrote about other
        users and shared conversation threads have your identity removed but the
        content stays attached to the other party's record, and booking/financial
        records are retained for accounting, tax, and dispute-resolution purposes
        with your identifying details removed from what we can. If you own an
        organization/agency account, you must transfer ownership before your
        account can be deleted.
      </Section>

      <Section title="Your Rights">
        Depending on where you live, you may have the right to access, correct, or
        request deletion of your personal information, and to object to or
        restrict certain processing. You can access and correct most of your
        information directly in the app, and request account deletion from
        Settings. For any other request, contact us using the details below.
      </Section>

      <Section title="California Consumer Rights">
        The California Consumer Privacy Act provides specific rights to California
        residents, including the right to know what personal information we
        collect, use, and disclose, and the right to request deletion of personal
        information we or our service providers store about you. We do not sell
        your personal information.
      </Section>

      <Section title="New York Residents">
        We maintain reasonable administrative, technical, and physical safeguards
        for personal information consistent with New York's SHIELD Act. If you
        believe your data has been affected by a security incident, contact us
        using the details below.
      </Section>

      <Section title="Children and Minor Talent">
        Our Services are not directed to children under 13, and we do not
        knowingly collect personal information from children under 13 without
        parental consent. 99 YARDS is a marketplace that connects buyers with
        talent, and we recognize that some talent profiles represent minors
        managed by a parent, guardian, or talent agency. If a profile represents a
        minor, the account must be created and managed by a parent, legal
        guardian, or a talent agency with appropriate authorization, and any
        payments due to a minor are subject to additional review before an
        agency's commission is released. If you believe a minor has an account
        that was not set up in accordance with this section, contact us using the
        details below.
      </Section>

      <Section title="Security">
        We use reasonable technical and organizational measures designed to
        protect your information. No method of transmission or storage is 100%
        secure, and we cannot guarantee absolute security.
      </Section>

      <Section title="Contacting Us">
        If you would like to understand more about this Policy, or to exercise any
        of your rights described above, contact us at contact@99yards.co.
      </Section>
    </Container>
  );
};

export default PrivacyPolicy;
